import React, { useState } from "react";

import clsx from "clsx";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { useCurrentlyUpdatingMetric } from "@features/custom-metrics/hooks/use-currently-updating-metric";

import { useFormulaBuilder } from "../../hooks/use-formula-builder";

import { FormulaActions } from "./formula-actions";
import { FormulaConfirmClear } from "./formula-confirm-clear";
import { FormulaToken } from "./formula-token";

interface Props {
  isDisabled: boolean;
}

export const FormulaInput = ({ isDisabled }: Props) => {
  const [isAnyDragging, setIsAnyDragging] = useState(false);
  const [hasConfirmedClear, setHasConfirmedClear] = useState(false);

  const { currentlyUpdatingMetric } = useCurrentlyUpdatingMetric();

  const { formula, remove, reorder, isFormulaValid, clear } =
    useFormulaBuilder();

  const clearFormula = () => {
    clear();
    setHasConfirmedClear(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) {
      return;
    }
    setIsAnyDragging(false);
    reorder(active.id, over.id);
  };

  const hasFormulaValue = formula.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <div
          className={clsx("text-xs font-medium tracking-wider", {
            "text-disabled": isDisabled,
          })}
        >
          FORMULA
          {!isFormulaValid && (
            <span className="ml-2 text-mars+1">*Invalid</span>
          )}
        </div>
        <FormulaActions
          isDisabled={isDisabled}
          onClear={() => setHasConfirmedClear(true)}
        />
      </div>
      {hasConfirmedClear ? (
        <FormulaConfirmClear
          onConfirmClear={clearFormula}
          onCancelClear={() => setHasConfirmedClear(false)}
        />
      ) : (
        <div
          className={clsx(
            {
              "border-gray-2": isFormulaValid,
              "border-mars+1": !isFormulaValid,
              "h-[100px]": currentlyUpdatingMetric?.isTemplate,
              "h-36": !currentlyUpdatingMetric?.isTemplate,
            },
            "border border-gray-2 rounded-md relative overflow-y-auto text-sm",
          )}
        >
          {!hasFormulaValue && (
            <div className="absolute-center text-primary flex flex-row w-[25rem] items-center">
              <div className="whitespace-nowraptext-md">
                Select metrics and operators in the right-hand panel
              </div>
              <i className="ml-2 fa-regular fa-arrow-right"></i>
            </div>
          )}
          <DndContext
            onDragStart={() => setIsAnyDragging(true)}
            collisionDetection={closestCenter}
            sensors={sensors}
            onDragEnd={onDragEnd}
          >
            <div className="p-3 flex flex-wrap gap-2 min-h-[40px]">
              <SortableContext
                disabled={isDisabled}
                strategy={rectSortingStrategy}
                items={formula}
              >
                {formula.map((token) => (
                  <FormulaToken
                    isDisabled={isDisabled}
                    key={token.id}
                    token={token}
                    onRemove={() => remove(token.id)}
                    isAnyDragging={isAnyDragging}
                  />
                ))}
              </SortableContext>
            </div>
          </DndContext>
        </div>
      )}
    </div>
  );
};
